import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

function DMSConnections() {
  const [connections, setConnections] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [connectionsData, statusData, logsData] = await Promise.all([
        api.getDMSConnections(),
        api.getDMSSyncStatus(),
        api.getDMSSyncLogs(),
      ]);
      setConnections(connectionsData);
      setSyncStatus(statusData);
      setSyncLogs(logsData);
    } catch (error) {
      console.error('Failed to load DMS data:', error);
      setMessage({ type: 'error', text: 'Failed to load DMS connections' });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWebhook = async () => {
    setGenerating(true);
    setMessage(null);

    try {
      const config = await api.generateWebhookConfig('webhook');
      setWebhookConfig(config);
      setShowInstructions(true);
      loadData();
    } catch (error) {
      console.error('Failed to generate webhook:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to generate webhook' });
    } finally {
      setGenerating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: '#22c55e',
      error: '#ef4444',
      pending: '#f59e0b',
      paused: '#64748b',
    };
    return colors[status] || '#64748b';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard!' });
    setTimeout(() => setMessage(null), 2000);
  };

  if (loading) {
    return <div style={styles.loading}>Loading DMS connections...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🔗 DMS Connections</h1>
      <p style={styles.subtitle}>
        Connect your Dealer Management System for real-time inventory and lead sync.
      </p>

      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          color: message.type === 'success' ? '#86efac' : '#fca5a5',
        }}>
          {message.text}
        </div>
      )}

      {/* Connection Status */}
      {syncStatus && (
        <div style={styles.statusCard}>
          <h2 style={styles.statusTitle}>Sync Status</h2>
          <div style={styles.statusGrid}>
            <div style={styles.statusItem}>
              <div style={styles.statusValue}>{syncStatus.connections_count}</div>
              <div style={styles.statusLabel}>Connections</div>
            </div>
            <div style={styles.statusItem}>
              <div style={{...styles.statusValue, color: '#22c55e'}}>
                {syncStatus.active_connections}
              </div>
              <div style={styles.statusLabel}>Active</div>
            </div>
            <div style={styles.statusItem}>
              <div style={styles.statusValue}>{syncStatus.total_records_processed}</div>
              <div style={styles.statusLabel}>Records Synced</div>
            </div>
            <div style={styles.statusItem}>
              <div style={{...styles.statusValue, color: syncStatus.total_records_failed > 0 ? '#ef4444' : '#22c55e'}}>
                {syncStatus.total_records_failed}
              </div>
              <div style={styles.statusLabel}>Failed</div>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Setup */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Webhook Integration</h2>
        
        {connections.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🔗</div>
            <div style={styles.emptyText}>No webhook configured yet</div>
            <button
              onClick={handleGenerateWebhook}
              disabled={generating}
              style={styles.generateButton}
            >
              {generating ? 'Generating...' : 'Generate Webhook URL'}
            </button>
          </div>
        ) : (
          <div style={styles.connectionsList}>
            {connections.map(conn => (
              <div key={conn.id} style={styles.connectionCard}>
                <div style={styles.connectionHeader}>
                  <div style={styles.connectionType}>{conn.dms_type.toUpperCase()}</div>
                  <div style={{
                    ...styles.connectionStatus,
                    backgroundColor: getStatusColor(conn.status),
                  }}>
                    {conn.status}
                  </div>
                </div>
                
                <div style={styles.connectionDetails}>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Method:</span>
                    <span style={styles.detailValue}>{conn.connection_method}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Last Sync:</span>
                    <span style={styles.detailValue}>{formatDate(conn.last_sync_at)}</span>
                  </div>
                </div>
              </div>
            ))}
            
            <button
              onClick={handleGenerateWebhook}
              disabled={generating}
              style={styles.regenerateButton}
            >
              🔄 Regenerate Webhook Credentials
            </button>
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && webhookConfig && (
        <div style={styles.modalOverlay} onClick={() => setShowInstructions(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>📋 Webhook Setup Instructions</h2>
            
            <div style={styles.instructions}>
              <p style={styles.instructionText}>
                Share these details with your DMS administrator or IT team:
              </p>
              
              <div style={styles.codeBlock}>
                <div style={styles.codeLabel}>Webhook URL</div>
                <div style={styles.codeContent}>
                  <code style={styles.code}>{webhookConfig.webhook_url}</code>
                  <button
                    onClick={() => copyToClipboard(webhookConfig.webhook_url)}
                    style={styles.copyButton}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div style={styles.codeBlock}>
                <div style={styles.codeLabel}>Webhook Secret</div>
                <div style={styles.codeContent}>
                  <code style={styles.code}>{webhookConfig.webhook_secret}</code>
                  <button
                    onClick={() => copyToClipboard(webhookConfig.webhook_secret)}
                    style={styles.copyButton}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div style={styles.instructionsBox}>
                <h3 style={styles.instructionsTitle}>Setup Steps:</h3>
                <pre style={styles.instructionsPre}>{webhookConfig.instructions}</pre>
              </div>
            </div>
            
            <button
              onClick={() => setShowInstructions(false)}
              style={styles.closeButton}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Sync Logs */}
      {syncLogs.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Recent Sync Activity</h2>
          
          <div style={styles.logsList}>
            {syncLogs.slice(0, 10).map((log, index) => (
              <div key={log.id || index} style={styles.logItem}>
                <div style={styles.logHeader}>
                  <span style={styles.logType}>{log.sync_type.toUpperCase()}</span>
                  <span style={{
                    ...styles.logStatus,
                    color: log.records_failed > 0 ? '#ef4444' : '#22c55e',
                  }}>
                    {log.records_failed > 0 ? '⚠️' : '✓'}
                  </span>
                </div>
                <div style={styles.logDetails}>
                  <span style={styles.logRecords}>
                    {log.records_processed} processed
                    {log.records_failed > 0 && `, ${log.records_failed} failed`}
                  </span>
                  <span style={styles.logTime}>{formatDate(log.completed_at)}</span>
                </div>
                {log.error_message && (
                  <div style={styles.logError}>{log.error_message}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '900px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: '0 0 24px 0',
    fontSize: '14px',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
  },
  statusCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #334155',
    marginBottom: '24px',
  },
  statusTitle: {
    color: '#f1f5f9',
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  },
  statusItem: {
    textAlign: 'center',
  },
  statusValue: {
    color: '#f1f5f9',
    fontSize: '28px',
    fontWeight: '700',
  },
  statusLabel: {
    color: '#64748b',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  section: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #334155',
    marginBottom: '24px',
  },
  sectionTitle: {
    color: '#f1f5f9',
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 32px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '16px',
    marginBottom: '16px',
  },
  generateButton: {
    padding: '12px 24px',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  connectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  connectionCard: {
    background: '#0f172a',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #334155',
  },
  connectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  connectionType: {
    color: '#f1f5f9',
    fontSize: '14px',
    fontWeight: '600',
  },
  connectionStatus: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#fff',
    textTransform: 'uppercase',
  },
  connectionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailRow: {
    display: 'flex',
    gap: '8px',
  },
  detailLabel: {
    color: '#64748b',
    fontSize: '13px',
  },
  detailValue: {
    color: '#94a3b8',
    fontSize: '13px',
  },
  regenerateButton: {
    padding: '10px 16px',
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '8px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '32px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid #334155',
  },
  modalTitle: {
    color: '#f1f5f9',
    margin: '0 0 24px 0',
    fontSize: '20px',
    fontWeight: '600',
  },
  instructions: {
    marginBottom: '24px',
  },
  instructionText: {
    color: '#94a3b8',
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  codeBlock: {
    background: '#0f172a',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
  },
  codeLabel: {
    color: '#64748b',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  codeContent: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  code: {
    color: '#86efac',
    fontSize: '13px',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    flex: 1,
  },
  copyButton: {
    padding: '6px 12px',
    background: '#334155',
    border: 'none',
    borderRadius: '4px',
    color: '#f1f5f9',
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  instructionsBox: {
    background: '#0f172a',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
  },
  instructionsTitle: {
    color: '#f1f5f9',
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600',
  },
  instructionsPre: {
    color: '#94a3b8',
    fontSize: '13px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    margin: 0,
  },
  closeButton: {
    padding: '12px 24px',
    background: 'var(--brand-primary, #3b82f6)',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  logsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  logItem: {
    background: '#0f172a',
    borderRadius: '8px',
    padding: '12px',
    border: '1px solid #334155',
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  logType: {
    color: 'var(--brand-primary, #3b82f6)',
    fontSize: '11px',
    fontWeight: '600',
  },
  logStatus: {
    fontSize: '14px',
  },
  logDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#64748b',
    fontSize: '12px',
  },
  logRecords: {},
  logTime: {},
  logError: {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #334155',
  },
};

export default DMSConnections;
